import type { ReactNode } from "react";

import type { PlaybackTimeRange, PlaybackTransport, VideoChannelSource } from "../Video";

export type AnnotationStudioLocale = "en" | "ko";

export type AnnotationStudioLocalizedText = Readonly<{
  en: string;
  ko: string;
}>;

/**
 * Product-supplied Studio copy. Plain strings are already localized by the product adapter;
 * locale maps let a locale-independent adapter defer the final selection to c6.
 */
export type AnnotationStudioMessage = string | AnnotationStudioLocalizedText;

export type AnnotationStudioLabels = Readonly<{
  collection: AnnotationStudioLocalizedText;
  sequence: AnnotationStudioLocalizedText;
  stream: AnnotationStudioLocalizedText;
  target: AnnotationStudioLocalizedText;
}>;

export type AnnotationPointKind = "include" | "exclude";

export interface NormalizedAnnotationPoint {
  readonly id: string;
  readonly kind: AnnotationPointKind;
  readonly x: number;
  readonly y: number;
}

export interface TrackingPreviewContourPoint {
  readonly x: number;
  readonly y: number;
}

export type TrackingPreviewContour = readonly TrackingPreviewContourPoint[];

export interface DisposableRenderResource {
  readonly id: string;
  dispose(): void;
}

/** Optional image-bearing capability used by captured-frame and tracking render descriptors. */
export interface AnnotationImageRenderResource extends DisposableRenderResource {
  readonly image: CanvasImageSource | string;
}

export type CapturedFrameRenderResource = AnnotationImageRenderResource;

export interface ReplayableRenderSourceDescriptor<
  TResource extends DisposableRenderResource = DisposableRenderResource,
> {
  readonly id: string;
  readonly revision: number;
  acquire(options: { signal: AbortSignal }): Promise<TResource>;
}

export interface AnnotationRenderSourceRef {
  readonly id: string;
  readonly revision: number;
}

export interface AnnotationMediaSource {
  readonly src: string;
  readonly poster?: string | null;
  /** Forwarded to the native video element; c6 intentionally has no cross-origin default. */
  readonly crossOrigin?: "anonymous" | "use-credentials";
  readonly durationSeconds?: number | null;
  /** Sizes the viewport until the displayed media reports its own size, which then wins. */
  readonly aspectRatio?: number | null;
}

export interface AnnotationSequence {
  readonly id: string;
  readonly label: string;
  readonly streamIds: readonly string[];
  /** Product-owned canonical episode duration, when available. */
  readonly durationSeconds?: number | null;
}

export interface AnnotationStudioSequenceActionContext {
  readonly sequence: AnnotationSequence;
  readonly streamId: string | null;
  readonly selected: boolean;
  readonly pinned: boolean;
}

export type AnnotationStudioSequenceActionMode = "available" | "persistent";

/**
 * Product-owned sequence action plus its Studio presentation policy.
 *
 * - `available`: shows the Studio-owned tracking availability indicator at rest. When `action`
 *   exists, the row swaps to it while hovered/focused and keeps it visible on no-hover inputs.
 * - `persistent`: shows `action` at rest so recovery actions such as stop/retry remain reachable.
 */
export type AnnotationStudioSequenceActionPresentation =
  | {
      readonly mode: "available";
      readonly action?: ReactNode;
    }
  | {
      readonly mode: "persistent";
      readonly action: ReactNode;
    };

/** A bare ReactNode remains a backward-compatible shorthand for a persistent action. */
export type AnnotationStudioSequenceActionResult =
  | ReactNode
  | AnnotationStudioSequenceActionPresentation;

export type AnnotationStudioSequenceActionRenderer = (
  context: AnnotationStudioSequenceActionContext,
) => AnnotationStudioSequenceActionResult;

/** Product-neutral sequence summary used by the optional paged directory path. */
export type AnnotationSequenceSummary = AnnotationSequence;

/** Inclusive sequence-content threshold used by the optional paged directory. */
export type SequenceDirectoryContentScope = "all" | "captured" | "annotated";

export interface SequenceDirectoryQuery {
  readonly streamId: string | null;
  /** Optional for compatibility with directory sources created before content filtering. */
  readonly contentScope?: SequenceDirectoryContentScope;
  /**
   * When present, limits the directory to the exact c6-derived sequence set that currently has
   * validation/save conflicts or unsaved annotation changes. An empty list is an active filter
   * with no matching sequences; omission disables the attention filter.
   */
  readonly attentionSequenceIds?: readonly string[];
}

export interface SequenceDirectoryStreamFacet {
  readonly streamId: string;
  readonly totalCount: number;
}

export type AnnotationSequenceDirectoryStatus = "idle" | "loading" | "ready" | "error";

export interface AnnotationSequenceDirectorySnapshot {
  readonly query: SequenceDirectoryQuery;
  readonly items: readonly AnnotationSequenceSummary[];
  /** Query-independent collection total supplied with the product's initial metadata. */
  readonly totalSequenceCount: number;
  /** Total rows matching the current query. Omit when the source cannot know it exactly. */
  readonly resultCount?: number;
  readonly streamFacets: readonly SequenceDirectoryStreamFacet[];
  readonly hasNextPage: boolean;
  readonly status: AnnotationSequenceDirectoryStatus;
  readonly isLoadingNextPage: boolean;
  readonly error?: AnnotationStudioLocalizedText;
  readonly nextPageError?: AnnotationStudioLocalizedText;
}

/**
 * Product-owned page/cache source for large collections. c6 intentionally does not know the
 * transport, cursor, or whether pages are backed by local IDs or a server summary endpoint.
 */
export interface AnnotationSequenceDirectorySource {
  getSnapshot(): AnnotationSequenceDirectorySnapshot;
  subscribe(listener: () => void): () => void;
  setQuery(query: SequenceDirectoryQuery): void;
  loadNextPage(): void;
  retry(): void;
  getCachedSequence(sequenceId: string): AnnotationSequenceSummary | null;
  ensureSequence(
    sequenceId: string,
    options: { signal: AbortSignal },
  ): Promise<AnnotationSequenceSummary>;
}

export interface AnnotationStream {
  readonly id: string;
  readonly label: string;
  /** Optional camera-wide fallback when media is not resolved per sequence/stream pair. */
  readonly media?: AnnotationMediaSource;
}

export interface AnnotationTarget {
  readonly id: string;
  readonly label: string;
  readonly color: number;
}

export interface AnnotationSetRef {
  readonly setId: string;
  readonly revision: number;
}

/** Opaque equality token. c6 never orders or increments this value. */
export type AnnotationSourceRevision = string | number;

export interface AnnotationMediaTarget {
  readonly sequenceId: string;
  readonly streamId: string;
}

export interface TimedLayerTiming {
  readonly sampling: "exact" | "nearest" | "hold";
  /** Positive values delay presentation relative to the master media clock. */
  readonly offsetSeconds?: number;
  readonly toleranceSeconds?: number;
}

export interface TimedSampleQuery extends TimedLayerTiming {
  /** Layer-local time after the Studio applies the layer offset. */
  readonly timeSeconds: number;
}

/**
 * Lazy time-indexed source. The product owns fetching, indexing, caching and dedupe; c6 requests
 * only the sample/range needed around the current transport time.
 */
export interface TimedSampleSource<TSample> {
  subscribe(listener: () => void): () => void;
  getSampleAt(query: TimedSampleQuery): TSample | null;
  prefetch?(range: PlaybackTimeRange, options: { signal: AbortSignal }): void | Promise<void>;
}

export interface TimedContourSample {
  readonly timestampSeconds: number;
  readonly targets: Readonly<Record<string, TrackingPreviewTargetResult>>;
}

export interface TimedRasterSample {
  readonly timestampSeconds: number;
  readonly frameSource: ReplayableRenderSourceDescriptor<AnnotationImageRenderResource>;
}

export type RasterInterpretation =
  | Readonly<{ type: "rgba" }>
  | Readonly<{ type: "alpha-mask"; channel: "alpha" | "luminance" }>
  | Readonly<{ type: "label-map" }>;

export interface RasterLayerAlignment {
  /** Normalized bounds in the upright base-media frame space. */
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type TimedRasterSource =
  | Readonly<{
      delivery: "samples";
      samples: TimedSampleSource<TimedRasterSample>;
    }>
  | Readonly<{
      delivery: "video";
      media: VideoChannelSource;
    }>;

interface TimedMediaLayerBase {
  /** Stable and unique within one MediaResultSource revision. */
  readonly id: string;
  readonly timing: TimedLayerTiming;
}

export type TimedMediaLayer =
  | Readonly<
      TimedMediaLayerBase & {
        kind: "contour";
        source: TimedSampleSource<TimedContourSample>;
      }
    >
  | Readonly<
      TimedMediaLayerBase & {
        kind: "raster";
        role: "mask" | "heatmap";
        source: TimedRasterSource;
        interpretation: RasterInterpretation;
        alignment: RasterLayerAlignment;
      }
    >;

export interface MediaResultSource {
  readonly id: string;
  readonly revision: AnnotationSourceRevision;
  readonly baseMedia: VideoChannelSource;
  readonly layers: readonly TimedMediaLayer[];
}

export interface AnnotationMediaResultBinding {
  readonly targetMedia: AnnotationMediaTarget;
  readonly source: MediaResultSource;
  readonly transport: PlaybackTransport;
}

export interface AnnotationFrameLocator {
  readonly frameId: string;
  readonly sequenceId: string;
  readonly streamId: string;
  readonly timestampSeconds: number;
}

export interface AnnotationFrameSummary extends AnnotationFrameLocator {
  readonly revision: AnnotationSourceRevision;
  readonly annotatedTargetIds: readonly string[];
}

export interface AnnotationSavedFrameIndexSnapshot {
  readonly set: AnnotationSetRef | null;
  readonly revision: AnnotationSourceRevision;
  readonly orderedFrames: readonly AnnotationFrameSummary[];
}

export interface AnnotationSavedFrameIndexSource {
  getSnapshot(): AnnotationSavedFrameIndexSnapshot;
  subscribe(listener: () => void): () => void;
  /** Optional O(1) lookup for large indexes; c6 falls back to the current snapshot array. */
  getFrameSummary?(frameId: string): AnnotationFrameSummary | null;
}

export interface MaterializeAnnotationFrameRequest {
  readonly frame: AnnotationFrameSummary;
  readonly expectedRevision: AnnotationSourceRevision;
}

export interface MaterializeAnnotationFrameResult {
  readonly frame: AnnotationStudioInitialFrame;
  readonly revision: AnnotationSourceRevision;
}

export interface AnnotationFrameMaterializer {
  materializeFrame(
    request: MaterializeAnnotationFrameRequest,
    options: { signal: AbortSignal },
  ): Promise<MaterializeAnnotationFrameResult>;
}

export interface AnnotationSavedFrameMutationReceipt {
  readonly set: AnnotationSetRef | null;
  readonly savedFrameIndexRevision: AnnotationSourceRevision;
}

export interface SavedFrameTargetAnnotation {
  readonly targetId: string;
  readonly points: readonly NormalizedAnnotationPoint[];
  readonly contours?: readonly TrackingPreviewContour[];
  readonly maskSource?: AnnotationRenderSourceRef;
}

export interface SavedAnnotationFrame {
  readonly frameId: string;
  readonly sequenceId: string;
  readonly streamId: string;
  readonly timestampSeconds: number;
  readonly sourceRevision: number;
  readonly targets: Readonly<Record<string, SavedFrameTargetAnnotation>>;
}

export interface SavedAnnotationSet {
  readonly setId: string;
  readonly revision: number;
  readonly orderedFrames: readonly SavedAnnotationFrame[];
}

export interface AnnotationWorkspaceSnapshot {
  readonly collectionId: string;
  readonly collectionLabel: string;
  readonly revision: number;
  readonly sequences: readonly AnnotationSequence[];
  readonly streams: readonly AnnotationStream[];
  readonly targets: readonly AnnotationTarget[];
  readonly annotationSets: readonly SavedAnnotationSet[];
}

export interface AnnotationWorkspaceSource {
  getSnapshot(): AnnotationWorkspaceSnapshot;
  subscribe(listener: () => void): () => void;
}

export interface AnnotationMediaResolutionContext {
  readonly workspace: AnnotationWorkspaceSnapshot;
  readonly sequence: AnnotationSequence;
  readonly stream: AnnotationStream;
}

/**
 * Resolves product-owned media for one sequence/stream pair. Returning `undefined` uses
 * `stream.media`; returning `null` explicitly marks that pair as unavailable. The resolver is a
 * synchronous, side-effect-free lookup for the current workspace snapshot. Publish a new
 * workspace revision when a signed URL or other resolved media value changes.
 */
export type AnnotationMediaResolver = (
  context: AnnotationMediaResolutionContext,
) => AnnotationMediaSource | null | undefined;

export type AnnotationDirtyScope =
  | Readonly<{ kind: "target"; frameId: string; targetId: string }>
  | Readonly<{ kind: "frame"; frameId: string }>
  | Readonly<{ kind: "set"; draftId: string }>;

export type AnnotationStudioLeaveReason = "collection-change" | "close";

export interface AnnotationStudioLeaveConfirmation {
  readonly reason: AnnotationStudioLeaveReason;
}

export interface AnnotationStudioExitResult {
  /** True only when the user explicitly continued past a non-ready set-validation result. */
  readonly validationOverridden: boolean;
}

export interface AnnotationStudioExitRequestResult extends AnnotationStudioExitResult {
  /** Product-owned request token that this result settles. */
  readonly requestId: number;
  /** Whether save/leave preflight completed and the product may continue exiting. */
  readonly approved: boolean;
}

export interface AnnotationStudioValidationReviewResult {
  /** Product-owned request token that this result settles. */
  readonly requestId: number;
  /** Whether the product may continue its pending navigation. */
  readonly approved: boolean;
  /** True only when the user explicitly continued past a non-ready validation result. */
  readonly validationOverridden: boolean;
}

export interface FrameTargetAnnotationDraft {
  readonly targetId: string;
  readonly points: readonly NormalizedAnnotationPoint[];
  readonly pointRevision: number;
  readonly contours?: readonly TrackingPreviewContour[];
  readonly maskSource?: AnnotationRenderSourceRef;
}

export interface AnnotationFrameDraft {
  readonly frameId: string;
  readonly sequenceId: string;
  readonly streamId: string;
  readonly timestampSeconds: number;
  readonly sourceRevision: number;
  readonly width: number;
  readonly height: number;
  readonly frameSource: AnnotationRenderSourceRef;
  readonly targets: Readonly<Record<string, FrameTargetAnnotationDraft>>;
}

export interface AnnotationSetDraft {
  readonly draftId: string;
  readonly base?: AnnotationSetRef;
  readonly orderedFrameIds: readonly string[];
  readonly frames: Readonly<Record<string, AnnotationFrameDraft>>;
  readonly activeFrameId: string | null;
  readonly dirtyScopes: readonly AnnotationDirtyScope[];
}

export interface AnnotationStudioInitialTargetAnnotation {
  readonly targetId: string;
  readonly points: readonly NormalizedAnnotationPoint[];
  readonly pointRevision?: number;
  readonly contours?: readonly TrackingPreviewContour[];
  readonly maskSource?: ReplayableRenderSourceDescriptor;
}

export interface AnnotationStudioInitialFrame extends CapturedFrame {
  readonly targets: Readonly<Record<string, AnnotationStudioInitialTargetAnnotation>>;
}

/**
 * Product-normalized, one-time controller hydration input.
 *
 * Backend DTOs stay in product adapters. The workspace identity prevents a session prepared for
 * one server snapshot from being applied to another, while replayable descriptors keep binary
 * frame and mask resources outside React-visible state.
 */
export interface AnnotationStudioInitialSession {
  readonly collectionId: string;
  readonly workspaceRevision: number;
  readonly draftId: string;
  readonly base?: AnnotationSetRef;
  readonly orderedFrames: readonly AnnotationStudioInitialFrame[];
  readonly activeFrameId: string | null;
  readonly selectedSequenceId?: string | null;
  readonly selectedStreamId?: string | null;
  readonly selectedTargetId?: string | null;
  readonly viewMode?: AnnotationStudioViewMode;
}

export type AnnotationOperation = "capture" | "segment" | "track" | "save" | "remove" | "render";

export class AnnotationOperationError extends Error {
  readonly name = "AnnotationOperationError";
  readonly operation: AnnotationOperation;
  readonly code: string;
  readonly userMessage?: string;
  readonly localizedUserMessage?: AnnotationStudioLocalizedText;
  readonly retryable: boolean;
  readonly conflict: boolean;

  constructor(
    operation: AnnotationOperation,
    code: string,
    options: {
      userMessage?: string;
      localizedUserMessage?: AnnotationStudioLocalizedText;
      retryable?: boolean;
      conflict?: boolean;
    } = {},
  ) {
    super(options.userMessage ?? options.localizedUserMessage?.en ?? code);
    this.operation = operation;
    this.code = code;
    this.userMessage = options.userMessage;
    this.localizedUserMessage = options.localizedUserMessage;
    this.retryable = options.retryable ?? false;
    this.conflict = options.conflict ?? false;
  }
}

export interface AnnotationOperationState {
  readonly pending: boolean;
  readonly error: AnnotationOperationError | null;
}

export interface AnnotationOperationAvailability {
  readonly allowed: boolean;
  /** Backward-compatible product-localized reason. */
  readonly reason?: string;
  /** Preferred when the driver is independent of the active Studio locale. */
  readonly localizedReason?: AnnotationStudioLocalizedText;
}

export interface AnnotationOperationContext {
  readonly workspace: AnnotationWorkspaceSnapshot;
  readonly sequenceId: string | null;
  readonly streamId: string | null;
  readonly targetId: string | null;
  readonly draft: AnnotationSetDraft | null;
}

export type AnnotationSetValidationStatus =
  | "idle"
  | "validating"
  | "ready"
  | "blocked"
  | "stale"
  | "error";

export type AnnotationSetValidationSeverity = "warning" | "error";

export interface AnnotationSetValidationIssue {
  readonly id: string;
  readonly code: string;
  readonly severity: AnnotationSetValidationSeverity;
  readonly message: AnnotationStudioLocalizedText;
  /** Every frame implicated by this issue. `frameId` remains the primary navigation target. */
  readonly affectedFrameIds?: readonly string[];
  readonly frameId?: string;
  /** Primary navigation target when the frame has not been materialized into the draft. */
  readonly location?: AnnotationFrameLocator;
  /** Related unloaded destinations. Stable frame identity is used for dedupe. */
  readonly affectedLocations?: readonly AnnotationFrameLocator[];
  readonly targetId?: string;
}

export interface ValidateAnnotationSetRequest {
  readonly collectionId: string;
  readonly workspaceRevision: number;
  readonly draftId: string;
  readonly base?: AnnotationSetRef;
  readonly targetIds: readonly string[];
  readonly frames: readonly AnnotationFrameDraft[];
  /** Captured only for canonical-source validation. */
  readonly sourceRevision?: AnnotationSourceRevision;
}

export interface AnnotationSetValidationResult {
  /** Product policy decides whether the set may move to its completion/finalization step. */
  readonly ready: boolean;
  readonly issues: readonly AnnotationSetValidationIssue[];
}

export interface AnnotationSetValidationDriver {
  /** Defaults to `draft`; canonical drivers validate their product-owned full saved set. */
  readonly validationSource?: "draft" | "canonical";
  getSourceRevision?(context: AnnotationOperationContext): AnnotationSourceRevision;
  getAvailability(context: AnnotationOperationContext): AnnotationOperationAvailability;
  validateAnnotationSet(
    request: ValidateAnnotationSetRequest,
    options: { signal: AbortSignal },
  ): Promise<AnnotationSetValidationResult>;
}

export interface AnnotationSetValidationState {
  readonly status: AnnotationSetValidationStatus;
  readonly availability: AnnotationOperationAvailability;
  readonly issues: readonly AnnotationSetValidationIssue[];
  readonly error: string | null;
  /** Opaque source revision captured by canonical validation. */
  readonly sourceRevision?: AnnotationSourceRevision;
}

export interface CaptureFrameRequest {
  readonly sequenceId: string;
  readonly streamId: string;
  readonly timestampSeconds: number;
}

export interface CapturedFrame {
  readonly frameId: string;
  readonly sequenceId: string;
  readonly streamId: string;
  readonly timestampSeconds: number;
  readonly sourceRevision: number;
  readonly width: number;
  readonly height: number;
  readonly frameSource: ReplayableRenderSourceDescriptor;
}

export interface SegmentRequest {
  readonly frameId: string;
  readonly sourceRevision: number;
  readonly targetId: string;
  readonly pointRevision: number;
  readonly points: readonly NormalizedAnnotationPoint[];
}

export interface SegmentTargetsRequestTarget {
  readonly targetId: string;
  readonly pointRevision: number;
  readonly points: readonly NormalizedAnnotationPoint[];
}

/**
 * Same-frame segmentation request for products that can evaluate several targets in one call.
 */
export interface SegmentTargetsRequest {
  readonly frameId: string;
  readonly sourceRevision: number;
  readonly targets: readonly SegmentTargetsRequestTarget[];
}

interface SegmentationResultIdentity {
  readonly frameId: string;
  readonly targetId: string;
  readonly pointRevision: number;
}

export type SegmentationResult = SegmentationResultIdentity &
  (
    | Readonly<{
        /**
         * Normalized contour geometry is the preferred overlay when both payloads are present.
         */
        contours: readonly TrackingPreviewContour[];
        maskSource?: ReplayableRenderSourceDescriptor;
      }>
    | Readonly<{
        contours?: never;
        maskSource: ReplayableRenderSourceDescriptor;
      }>
  );

export type TrackingPreviewStatus =
  | "idle"
  | "starting"
  | "running"
  | "complete"
  | "stopped"
  | "error";

export type TrackingTargetConditioning = SavedFrameTargetAnnotation;

export interface TrackingConditioningKeyframe {
  readonly frameId: string;
  readonly sequenceId: string;
  readonly streamId: string;
  readonly timestampSeconds: number;
  readonly sourceRevision: number;
  readonly targets: Readonly<Record<string, TrackingTargetConditioning>>;
}

/** @deprecated Use the product-neutral AnnotationMediaTarget. */
export type TrackingPreviewTargetMedia = AnnotationMediaTarget;

export interface TrackingPreviewRequest {
  readonly targetMedia: Readonly<TrackingPreviewTargetMedia>;
  readonly conditioningKeyframes: readonly TrackingConditioningKeyframe[];
  readonly conditioningRevision?: AnnotationSourceRevision;
}

export interface TrackingPreviewTargetResult {
  readonly targetId: string;
  readonly contours: readonly TrackingPreviewContour[];
}

export type TrackingFrameRenderResource = AnnotationImageRenderResource;

export interface TrackingPreviewStreamFrame {
  readonly frameIndex: number;
  readonly timestampSeconds: number;
  /**
   * Optional normalized geometry for Studio-owned preview rendering.
   * Drivers that only stream progress metadata may omit it.
   */
  readonly targets?: Readonly<Record<string, TrackingPreviewTargetResult>>;
  /**
   * Stream-event-only descriptor. The controller retains only its lightweight ref in public
   * latest-frame state and keeps only the newest replayable descriptor for display.
   */
  readonly frameSource?: ReplayableRenderSourceDescriptor<TrackingFrameRenderResource>;
}

export interface TrackingPreviewFrameMetadata {
  readonly frameIndex: number;
  readonly timestampSeconds: number;
  readonly targets?: Readonly<Record<string, TrackingPreviewTargetResult>>;
  /** Optional ref into the controller-owned latest tracking source slot. */
  readonly frameSourceRef?: AnnotationRenderSourceRef;
}

/** @deprecated Move execution to the product and pass AnnotationMediaResultBinding for display. */
export interface AnnotationTrackingPreviewDriver {
  /** Defaults to request-keyframes for existing consumers. */
  readonly conditioningSource?: "request-keyframes" | "canonical";
  getConditioningRevision?(
    context: AnnotationOperationContext,
    targetMedia: TrackingPreviewTargetMedia,
  ): AnnotationSourceRevision;
  getTargetAvailability?(
    context: AnnotationOperationContext,
    targetMedia: TrackingPreviewTargetMedia,
  ): AnnotationOperationAvailability;
  getAvailability(context: AnnotationOperationContext): AnnotationOperationAvailability;
  previewTracking(
    request: TrackingPreviewRequest,
    options: { signal: AbortSignal },
  ): AsyncIterable<TrackingPreviewStreamFrame>;
}

export interface AnnotationTrackingPreviewState {
  readonly status: TrackingPreviewStatus;
  /** Episode/camera pair whose media receives the transient preview result. */
  readonly targetMedia: Readonly<TrackingPreviewTargetMedia> | null;
  readonly framesSeen: number;
  readonly latestFrame: TrackingPreviewFrameMetadata | null;
  /** Source ref of the newest producer frame while the live preview session is active. */
  readonly latestFrameSourceRef: AnnotationRenderSourceRef | null;
  /**
   * Compatibility input for custom controllers. The default controller leaves this undefined;
   * presentation consumes only its last entry when `latestFrame` is unavailable.
   */
  readonly frames?: readonly TrackingPreviewFrameMetadata[];
  readonly error: AnnotationOperationError | null;
}

export interface SaveAnnotationFrameRequest {
  readonly collectionId: string;
  readonly workspaceRevision: number;
  readonly draftId: string;
  readonly base?: AnnotationSetRef;
  readonly frameIndex: number;
  readonly frame: AnnotationFrameDraft;
}

export interface DeleteAnnotationFrameRequest {
  readonly collectionId: string;
  readonly workspaceRevision: number;
  readonly draftId: string;
  readonly base: AnnotationSetRef;
  readonly frameId: string;
}

export interface AnnotationMutationResult<T> {
  readonly entity: T;
  readonly workspaceRevision: number;
}

/**
 * Mode-independent segmentation capability used to derive display overlays from stored points.
 * Products that persist contours or mask sources can omit this driver in review mode.
 */
export interface AnnotationSegmentationDriver {
  getAvailability(context: AnnotationOperationContext): AnnotationOperationAvailability;
  segment(request: SegmentRequest, options: { signal: AbortSignal }): Promise<SegmentationResult>;
  /**
   * Optional same-frame batch capability. Results are matched by target identity, not array order.
   * Drivers that omit it retain the single-target `segment` flow.
   */
  segmentTargets?(
    request: SegmentTargetsRequest,
    options: { signal: AbortSignal },
  ): Promise<readonly SegmentationResult[]>;
}

export interface AnnotationAuthoringDriver extends AnnotationSegmentationDriver {
  captureFrame(
    request: CaptureFrameRequest,
    options: { signal: AbortSignal },
  ): Promise<CapturedFrame>;
  saveAnnotationFrame(
    request: SaveAnnotationFrameRequest,
    options: { signal: AbortSignal },
  ): Promise<AnnotationMutationResult<SavedAnnotationSet>>;
  deleteAnnotationFrame(
    request: DeleteAnnotationFrameRequest,
    options: { signal: AbortSignal },
  ): Promise<AnnotationMutationResult<SavedAnnotationSet>>;
}

/** Mutation contract for the optional saved-frame index path. */
export interface AnnotationIndexedAuthoringDriver extends AnnotationSegmentationDriver {
  captureFrame(
    request: CaptureFrameRequest,
    options: { signal: AbortSignal },
  ): Promise<CapturedFrame>;
  saveAnnotationFrame(
    request: SaveAnnotationFrameRequest,
    options: { signal: AbortSignal },
  ): Promise<AnnotationMutationResult<AnnotationSavedFrameMutationReceipt>>;
  deleteAnnotationFrame(
    request: DeleteAnnotationFrameRequest,
    options: { signal: AbortSignal },
  ): Promise<AnnotationMutationResult<AnnotationSavedFrameMutationReceipt>>;
}

export type AnnotationStudioViewMode = "browse" | "annotate";

export interface AnnotationStudioControllerSnapshot {
  readonly connected: boolean;
  readonly mode: "authoring" | "review";
  /** Present on the default controller. Omission keeps the legacy validate-before-save order. */
  readonly exitPreflightOrder?: "validate-then-save" | "save-then-validate";
  readonly locale: AnnotationStudioLocale;
  readonly labels: AnnotationStudioLabels;
  readonly workspace: AnnotationWorkspaceSnapshot;
  /** Present only for the optional large-collection directory path. */
  readonly sequenceDirectory?: AnnotationSequenceDirectorySnapshot | null;
  /** Selected/cached sequence shown above results when it falls outside the active query. */
  readonly pinnedSequence?: AnnotationSequenceSummary | null;
  /** Present only for the optional revisioned saved-frame index path. */
  readonly savedFrameIndex?: AnnotationSavedFrameIndexSnapshot | null;
  readonly selectedSequenceId: string | null;
  readonly selectedStreamId: string | null;
  readonly selectedTargetId: string | null;
  readonly viewMode: AnnotationStudioViewMode;
  readonly draft: AnnotationSetDraft | null;
  readonly isDirty: boolean;
  /** Frames with an unresolved `save` operation error whose normalized error has `conflict=true`. */
  readonly saveConflictFrameIds?: readonly string[];
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly leaveConfirmation: AnnotationStudioLeaveConfirmation | null;
  /** Present only in authoring mode; c6 uses it for mutation guards and disabled help text. */
  readonly authoringAvailability?: AnnotationOperationAvailability;
  /** Present when point-only targets can derive a display overlay in the current context. */
  readonly segmentationAvailability?: AnnotationOperationAvailability;
  readonly trackingAvailable: boolean;
  /** Present on the default controller; omitted custom controllers retain request-keyframe mode. */
  readonly trackingUsesCanonicalConditioning?: boolean;
  /** Product-owned availability; Studio adds saved-keyframe and dirty-scope checks. */
  readonly trackingAvailability?: AnnotationOperationAvailability;
  readonly trackingPreview: AnnotationTrackingPreviewState;
  readonly operations: Readonly<Record<AnnotationOperation, AnnotationOperationState>>;
  /** Present only when a product supplies annotation-set validation policy. */
  readonly annotationSetValidation?: AnnotationSetValidationState | null;
}

export interface AnnotationStudioController {
  getSnapshot(): AnnotationStudioControllerSnapshot;
  subscribe(listener: () => void): () => void;
  connect(): () => void;
  dispose(): void;
  requestLeave(reason: AnnotationStudioLeaveReason): Promise<boolean>;
  confirmLeave(): void;
  cancelLeave(): void;
  updatePresentation(
    locale: AnnotationStudioLocale,
    labels?: Partial<AnnotationStudioLabels>,
  ): void;
  selectSequence(sequenceId: string): void;
  setSequenceDirectoryQuery?(query: SequenceDirectoryQuery): void;
  loadNextSequencePage?(): void;
  retrySequenceDirectory?(): void;
  ensureSequence?(sequenceId: string): Promise<boolean>;
  materializeAnnotationFrame?(frameId: string): Promise<boolean>;
  /** Pins UI-owned clean materializations (for example, the comparison preview) in the LRU. */
  setRetainedMaterializedFrameIds?(frameIds: readonly string[]): void;
  selectStream(streamId: string): void;
  selectTarget(targetId: string): void;
  captureFrame(timestampSeconds?: number): Promise<void>;
  removeCapturedFrame?(frameId?: string): Promise<void>;
  setActiveCapturedFrame(frameId: string): void;
  selectCapturedFrame?(frameId: string): void;
  showVideo(): void;
  showCapturedFrame(): void;
  addPoint(kind: AnnotationPointKind, x: number, y: number): void;
  removePoint(pointId: string): void;
  undoPoint(): void;
  undo(): void;
  redo(): void;
  clearTarget(targetId: string): void;
  clearAllTargets(): void;
  discardFrameChanges(frameId: string): void;
  clearCurrentTarget(): void;
  /** Computes a derived overlay without changing point content or dirty state. */
  segment(): Promise<void>;
  /** Computes a derived overlay without changing point content or dirty state. */
  segmentTarget(frameId: string, targetId: string): Promise<void>;
  /**
   * Computes several same-frame overlays together when supported. Custom controllers may omit
   * this method; Studio then retains the single-target scheduler.
   */
  segmentTargets?(frameId: string, targetIds: readonly string[]): Promise<void>;
  /**
   * Starts against an explicit episode/camera pair. Omitting it preserves the legacy active-frame
   * target for existing controller consumers.
   */
  startTrackingPreview(targetMedia?: TrackingPreviewTargetMedia): Promise<void>;
  /**
   * Optionally pauses the active producer without discarding its target or latest display frame.
   * Calling `startTrackingPreview` again for the same target resumes that producer. Controllers
   * that omit this compatibility capability retain the legacy hard-stop behavior.
   */
  pauseTrackingPreview?(): void;
  /** Optional synchronous product-policy lookup used by contextual quick actions. */
  getTrackingPreviewAvailability?(
    targetMedia: TrackingPreviewTargetMedia,
  ): AnnotationOperationAvailability;
  stopTrackingPreview(): void;
  /** Optional for custom controllers; the default controller resolves `frameSource` capabilities. */
  getCapturedFrameRenderResource?(
    ref: AnnotationRenderSourceRef,
  ): CapturedFrameRenderResource | null;
  subscribeCapturedFrameRenderResource?(listener: () => void): () => void;
  getTrackingFrameRenderResource(
    ref: AnnotationRenderSourceRef,
  ): TrackingFrameRenderResource | null;
  /**
   * Selects the one tracking frame source that should currently be decoded for display. Custom
   * controllers that eagerly manage their own resource cache may omit this method.
   */
  prepareTrackingFrameRenderResource?(ref: AnnotationRenderSourceRef | null): void;
  subscribeTrackingFrameRenderResource(listener: () => void): () => void;
  /** Optional for custom controllers; falls back to `AnnotationStream.media` when absent. */
  resolveMediaSource?(sequenceId: string, streamId: string): AnnotationMediaSource | null;
  saveAnnotationFrame(
    frameId: string,
    options?: { readonly validateAfterSave?: boolean },
  ): Promise<void>;
  /** Optional capability; frame persistence remains owned by saveAnnotationFrame. */
  validateAnnotationSet?(): Promise<void>;
}

type AnnotationStudioControllerBaseOptions = {
  readonly workspaceSource: AnnotationWorkspaceSource;
  readonly initialSession?: AnnotationStudioInitialSession;
  readonly locale: AnnotationStudioLocale;
  readonly labels?: Partial<AnnotationStudioLabels>;
  readonly resolveMediaSource?: AnnotationMediaResolver;
  /** Optional mode-independent overlay derivation, required for point-only review hydration. */
  readonly segmentation?: AnnotationSegmentationDriver;
  /** @deprecated Run tracking in the product and bind its result through MediaAnnotationStudio. */
  readonly trackingPreview?: AnnotationTrackingPreviewDriver;
  readonly annotationSetValidation?: AnnotationSetValidationDriver;
  /** Defaults to validate-then-save. save-then-validate requires canonical validation. */
  readonly exitPreflightOrder?: "validate-then-save" | "save-then-validate";
};

type AnnotationStudioModeOptions<TAuthoring> =
  | { readonly mode: "authoring"; readonly authoring: TAuthoring }
  | { readonly mode: "review"; readonly authoring?: never };

type StaticAnnotationCollectionOptions = {
  readonly sequenceDirectory?: never;
  readonly savedFrameIndex?: never;
  readonly frameMaterializer?: never;
};

type AsyncAnnotationCollectionOptions = {
  readonly sequenceDirectory: AnnotationSequenceDirectorySource;
  readonly savedFrameIndex: AnnotationSavedFrameIndexSource;
  readonly frameMaterializer: AnnotationFrameMaterializer;
};

export type AnnotationStudioControllerOptions = AnnotationStudioControllerBaseOptions &
  (
    | (StaticAnnotationCollectionOptions & AnnotationStudioModeOptions<AnnotationAuthoringDriver>)
    | (AsyncAnnotationCollectionOptions &
        AnnotationStudioModeOptions<AnnotationIndexedAuthoringDriver>)
  );
